import { v4 as uuidv4 } from 'uuid'
import { ssoStorage } from './ssoStorage'
import type { SSOClientConfig } from './types'

const POPUP_AUTH_STATE = 'popup_auth=true'

export function generateOAuthState(clientName: string): string {
  const nonce = uuidv4()
  ssoStorage.setStateNonce(nonce, clientName)
  return `${nonce}:${clientName}`
}

interface SignInOAuthState {
  /**
   * True when the state carries the nonce that /sso/:clientName stored before
   * sending the user to Cognito, i.e. this sign-in is that landing's launch.
   */
  startedAtSSOLanding: boolean
  /**
   * The client config resolved from the BFF registry at landing time. Only
   * returned when the registry spells the client name the way the URL did, so
   * a landing can be confirmed above even when this is undefined.
   */
  client?: SSOClientConfig
}

/** Validates the OAuth state nonce, consuming it, and says what it proves. */
function readSignInOAuthState(state: string | null): SignInOAuthState {
  if (!state || state === POPUP_AUTH_STATE) return { startedAtSSOLanding: false }
  const separatorIndex = state.indexOf(':')
  if (separatorIndex === -1) {
    ssoStorage.clearStateNonce()
    return { startedAtSSOLanding: false }
  }

  const nonce = state.substring(0, separatorIndex)
  const clientName = state.substring(separatorIndex + 1)
  const stored = ssoStorage.getOAuthState()

  if (!stored || stored.nonce !== nonce || stored.clientName !== clientName) {
    // Clear even when only one of the nonce/client keys remains, so a
    // partially-cleared localStorage can't leave stale OAuth state behind.
    ssoStorage.clearStateNonce()
    return { startedAtSSOLanding: false }
  }

  ssoStorage.clearStateNonce()
  const resolvedConfig = ssoStorage.getContext()?.resolvedConfig
  return {
    startedAtSSOLanding: true,
    client: resolvedConfig?.clientName === clientName ? resolvedConfig : undefined,
  }
}

/**
 * Brings the stored SSO context in line with how this sign-in started.
 *
 * Only a sign-in that started at /sso/:clientName launched a patient. Any
 * other way back in (a MyApps tile, the hosted UI, a bookmark) must not reopen
 * the previous launch's patient, which would otherwise still be in the stored
 * context and be loaded on New Script.
 *
 * Call it only for a sign-in that is about to be completed. A real popup
 * re-authentication must not call it, so it keeps the current patient; a
 * popup state arriving outside a popup is an ordinary sign-in and drops it.
 */
export function applySignInToSSOContext(state: string | null): void {
  const { startedAtSSOLanding, client } = readSignInOAuthState(state)
  if (client) {
    ssoStorage.mergeContext({
      clientName: client.clientName,
      providerId: client.providerId,
      providerPlatform: client.providerPlatform,
      providerType: client.providerType,
      resolvedConfig: client,
    })
  }
  if (!startedAtSSOLanding) {
    ssoStorage.clearExternalPatient()
  }
}
